/**
 * ResourcePicker — a reusable combobox that picks a person from the Clockify-synced resource
 * directory (`/api/resources`).
 *
 * Used anywhere a company resource needs to be chosen: project team assignment (PM / SA / Lead) on
 * the create-project wizard, the approval modal, the intake wizard, the migration team step, and
 * per-project team & resource assignment (UnifiedTeamSection / SwapResourceDialog). Fetches up to
 * 200 resources and filters client-side by name / email / designation inside the popover.
 * `onChange` returns the selected resource's id and name so callers can store either; clearing
 * calls `onChange("", "")`.
 *
 * ## Layout contract
 *
 * The trigger is a form field: same height, border, radius and placeholder colour as `Input` /
 * `DatePicker`, full width of its container and `min-w-0`, so it sits flush in grid rows and
 * never forces a column wider. Everything else lives in a PORTALLED popover — opening it never
 * pushes, resizes or clips the surrounding layout (dialogs with `overflow-y-auto` included).
 * Option rows are a fixed two lines; every piece of text truncates instead of wrapping.
 *
 * The popover is `modal`: inside a Radix Dialog a non-modal popover is portalled outside the
 * dialog's scroll lock, which swallows wheel / touch scrolling in the list. Modal makes the
 * popover the active lock (and traps focus in it), so the list scrolls and Esc returns focus to
 * the trigger.
 *
 * ## Which people are listed — `requireAssignable`
 *
 * Two different questions get conflated easily:
 *   - "is this a delivery-team member who logs time?"     → `assignable` (active AND submits
 *     timesheets)
 *   - "can this person be named as a lead on a project?"  → `active` only
 *
 * The filter is driven by the ROLE being filled, not by capacity (capacity allocation is out of
 * MVP scope — Task #10 — and is no longer collected anywhere). Named-lead roles (SA / PM /
 * Leadership / …) are often senior staff who do not submit timesheets, so gating them on
 * `submits_timesheets` would hide exactly the people those roles exist to record. Call sites
 * filling a named-lead role pass `requireAssignable={!isNamedLeadRole(role)}` (i.e. `false`).
 *
 * `active: true` is ALWAYS sent and is never relaxed by this prop: inactive/departed people must
 * never be selectable for any role.
 *
 * ## Delivery pool — `pool` (T-09)
 *
 * By default the picker sends `pool=delivery`: only people whose Clockify POD Assignment is in the
 * SSM-configured delivery pool (`/deliverpro/config/assignable-pods`). Pass `pool="all"` to list
 * the whole active directory. If the current `value` is someone OUTSIDE the pool (e.g. a lead
 * chosen before T-09 shipped), a one-off unpooled lookup resolves their name so the trigger never
 * silently shows the placeholder for a set value.
 *
 * ## Role filter — `roleOnProject` (2026-10-22)
 *
 * Pass the wire role being filled (e.g. `Solution Architect`) and the PRIMARY list is requested
 * with `role_on_project`, so the server returns only people whose title fits it: Solution
 * Architect → SA titles, Project Manager → PM / coordinator / product manager, Engineer →
 * developers / DevOps (`ROLE_DESIGNATION_PATTERNS`, packages/shared). Other roles are unfiltered
 * server-side. Legacy / non-catalog role values are not sent (they would be a 400).
 * The out-of-pool fallback name lookup never applies the filter, so an existing holder whose title
 * does not match still shows by name on the trigger.
 *
 * ## Current projects (T-07)
 *
 * The picker requests `include=assignments`; every option's second line shows "Not on any
 * project" or "On N projects: A, B +N" as TEXT (never colour alone). The full list with roles is
 * in that line's `title` tooltip and in screen-reader-only text, and a fixed-height footer shows it
 * for the HIGHLIGHTED option (arrow keys or hover) so sighted keyboard users get it too — without
 * the row itself growing.
 */
import { useId, useMemo, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { Check, ChevronDown, X } from "lucide-react";

import { useResources } from "@/hooks/useResources";
import type { Resource, ResourceCurrentProject } from "@/types";
import { PROJECT_ROLE_VALUES, projectRoleLabel } from "@/types/intake";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";

export interface ResourcePickerProps {
  /** Put on the trigger, so an existing `<Label htmlFor>` keeps naming the control. */
  id?: string;
  /** Currently-selected resource id. */
  value?: string;
  /** Called with the selected resource's id and display name; `("", "")` when cleared. */
  onChange: (resourceId: string, resourceName: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /**
   * Require the person to be *assignable* — active AND submitting timesheets. Default `true`.
   *
   * Pass `false` when filling a named-lead role (SA / PM / Lead Engineer), which is not
   * time-tracked. Active-only filtering still applies either way. See the note in this file's
   * docstring.
   */
  requireAssignable?: boolean;
  /**
   * Which population to list. Default `"delivery"` — the SSM-configured delivery pool (T-09).
   * `"all"` lists the whole active directory.
   */
  pool?: "delivery" | "all";
  /** Show a clear (×) button on the trigger while a value is set. Default `true`. */
  clearable?: boolean;
  /**
   * The role being filled (wire value, e.g. `Solution Architect`). Narrows the list to people
   * whose title fits it — see "Role filter" above. Omit for an unfiltered list.
   */
  roleOnProject?: string;
}

/**
 * Roles the SERVER filters by designation — mirrors the keys of `ROLE_DESIGNATION_PATTERNS`
 * (packages/shared/constants/role-designation-filters.ts). Used ONLY to word the empty-state hint;
 * the filtering itself is server-side, so a drift here can only mis-word a hint.
 */
const DESIGNATION_FILTERED_ROLES: ReadonlySet<string> = new Set([
  "Solution Architect",
  "Project Manager",
  "Engineer",
]);

/** "Solution Architects" / "Project Managers" / "Engineers" for the empty-state hint. */
function rolePlural(role: string): string {
  return `${projectRoleLabel(role)}s`;
}

/** How many project names are shown inline before "+N". */
const MAX_INLINE_PROJECTS = 2;

export interface CurrentProjectsSummary {
  /** Visible secondary-line text. */
  text: string;
  /** Every project with its role(s) — tooltip / screen-reader / footer text. */
  full: string;
  /** Distinct project count. */
  count: number;
  /** True when some names were replaced by "+N". */
  truncated: boolean;
}

/**
 * Summarise `current_projects` for an option row. Returns null when the field is absent (the list
 * was fetched without `include=assignments`), so nothing is rendered rather than a false
 * "Not on any project".
 *
 * Counts DISTINCT projects: the API returns one entry per role row, so a PM + Engineer on one
 * project is "On 1 project", with both roles in the full text.
 */
export function summarizeCurrentProjects(
  projects: ResourceCurrentProject[] | undefined | null,
): CurrentProjectsSummary | null {
  if (!projects) return null;

  const byKey = new Map<string, { name: string; roles: string[] }>();
  for (const p of projects) {
    const role = projectRoleLabel(p.role_on_project);
    const existing = byKey.get(p.jira_key);
    if (existing) {
      if (role && !existing.roles.includes(role)) existing.roles.push(role);
    } else {
      byKey.set(p.jira_key, {
        name: p.title?.trim() || p.jira_key,
        roles: role ? [role] : [],
      });
    }
  }

  const entries = Array.from(byKey.values());
  const count = entries.length;
  if (count === 0) {
    return {
      text: "Not on any project",
      full: "Not on any project",
      count: 0,
      truncated: false,
    };
  }

  const shown = entries.slice(0, MAX_INLINE_PROJECTS).map((e) => e.name);
  const more = count - shown.length;
  const text = `On ${count} project${count === 1 ? "" : "s"}: ${shown.join(", ")}${
    more > 0 ? ` +${more}` : ""
  }`;
  const full = entries
    .map((e) =>
      e.roles.length > 0 ? `${e.name} (${e.roles.join(", ")})` : e.name,
    )
    .join("; ");

  return { text, full, count, truncated: more > 0 };
}

/** Up to two initials (first + last word) for the avatar bubble. */
function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  const first = words[0].charAt(0);
  const last = words.length > 1 ? words[words.length - 1].charAt(0) : "";
  return `${first}${last}`.toUpperCase();
}

/** Muted descriptor: "designation · pod(s)". Empty string when neither is known. */
function describe(r: Resource): string {
  const title = r.designation ?? r.role_title ?? r.email ?? "";
  const pods = r.pod_assignment?.filter(Boolean).join(", ") ?? "";
  return [title, pods].filter(Boolean).join(" · ");
}

/**
 * Substring (AND over whitespace-separated terms) match on the item's keywords — name, email,
 * designation. Predictable "type what you see" search; cmdk's default fuzzy scorer would also
 * match scattered letters across emails.
 */
function filterResources(
  _value: string,
  search: string,
  keywords?: string[],
): number {
  const haystack = (keywords ?? []).join(" ").toLowerCase();
  const terms = search.toLowerCase().split(/\s+/).filter(Boolean);
  return terms.every((t) => haystack.includes(t)) ? 1 : 0;
}

function PersonAvatar({
  name,
  size = "md",
}: {
  name: string;
  size?: "sm" | "md";
}): JSX.Element {
  return (
    <Avatar
      className={size === "sm" ? "h-6 w-6" : "h-7 w-7"}
      aria-hidden="true"
    >
      <AvatarFallback
        className={cn(
          "bg-primary/10 font-semibold text-primary",
          size === "sm" ? "text-[10px]" : "text-[11px]",
        )}
      >
        {initials(name)}
      </AvatarFallback>
    </Avatar>
  );
}

export function ResourcePicker({
  id,
  value,
  onChange,
  placeholder = "Select a resource…",
  disabled,
  requireAssignable = true,
  pool = "delivery",
  clearable = true,
  roleOnProject,
}: ResourcePickerProps): JSX.Element {
  const autoId = useId();
  const triggerId = id ?? autoId;
  const valueId = `${triggerId}-value`;
  const triggerRef = useRef<HTMLButtonElement>(null);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  // cmdk's highlighted item value (a resource id) — drives the full-projects footer.
  const [active, setActive] = useState("");

  // Only catalog roles go on the wire — a legacy value (e.g. "Developer") would be a 400.
  const wireRole =
    roleOnProject &&
    (PROJECT_ROLE_VALUES as readonly string[]).includes(roleOnProject)
      ? roleOnProject
      : undefined;
  const roleFiltered =
    wireRole !== undefined && DESIGNATION_FILTERED_ROLES.has(wireRole);

  // `active: true` always — inactive/departed Clockify users are excluded server-side for every
  // role. `assignable` additionally requires submits_timesheets, which is correct for delivery-team
  // roles and wrong for named leads.
  const { data, isLoading, isError, refetch } = useResources({
    page_size: 200,
    active: true,
    assignable: requireAssignable,
    includeAssignments: true,
    pool: pool === "delivery" ? "delivery" : undefined,
    roleOnProject: wireRole,
  });

  const resources = useMemo<Resource[]>(() => data?.data ?? [], [data]);
  const selectedInList = resources.find((r) => r.id === value);

  // Backward compatibility for callers that pre-fill `value` with someone outside the delivery
  // pool — or, with a role filter, someone whose title does not fit the role: resolve the name
  // from the unpooled, UNFILTERED directory so the trigger shows who is selected. Only fires in
  // that case — otherwise this query stays disabled. Never pass `roleOnProject` here.
  const needsFallback =
    (pool === "delivery" || roleFiltered) &&
    Boolean(value) &&
    Boolean(data) &&
    !selectedInList;
  const fallback = useResources({
    page_size: 200,
    active: true,
    assignable: requireAssignable,
    enabled: needsFallback,
  });

  const selected =
    selectedInList ??
    (needsFallback
      ? fallback.data?.data?.find((r) => r.id === value)
      : undefined);
  const resolving =
    Boolean(value) && !selected && (isLoading || fallback.isLoading);
  const showClear = clearable && Boolean(value) && !disabled;

  const hasAssignmentData = resources.some(
    (r) => r.current_projects !== undefined,
  );
  const highlighted = active
    ? resources.find((r) => r.id === active)
    : undefined;
  const highlightedSummary = highlighted
    ? summarizeCurrentProjects(highlighted.current_projects)
    : null;

  function handleOpenChange(next: boolean): void {
    setOpen(next);
    if (next) {
      // Start on the current selection when it is listed; otherwise cmdk highlights the first row.
      setActive(selectedInList ? selectedInList.id : "");
    } else {
      setSearch("");
    }
  }

  function handleTriggerKeyDown(e: KeyboardEvent<HTMLButtonElement>): void {
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      e.preventDefault();
      handleOpenChange(true);
    }
  }

  function handleClear(): void {
    onChange("", "");
    // The clear button unmounts with the value — keep focus on the field, not <body>.
    triggerRef.current?.focus();
  }

  const selectedMeta = selected ? describe(selected) : "";

  return (
    // `[contain:inline-size]`: the field never reports its contents as its intrinsic width, so a
    // long selected name cannot widen a grid / flex parent that lacks `min-w-0` (which is what
    // pushed the trigger past the card edge). The width always comes from the container.
    <div className="relative w-full min-w-0 max-w-full [contain:inline-size]">
      <Popover open={open} onOpenChange={handleOpenChange} modal>
        <PopoverTrigger asChild>
          <button
            ref={triggerRef}
            id={triggerId}
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-describedby={selected ? valueId : undefined}
            disabled={disabled}
            onKeyDown={handleTriggerKeyDown}
            data-empty={!selected || undefined}
            className={cn(
              // Mirrors `Input` / `DatePicker` so the control sits flush in the same form row.
              "flex h-[42px] w-full min-w-0 items-center gap-2 overflow-hidden rounded-[var(--radius-input)] border border-outlines bg-white pl-3 pr-3 text-left text-sm text-text-primary transition-colors",
              "focus:outline-none focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary data-[state=open]:border-primary data-[state=open]:ring-1 data-[state=open]:ring-primary",
              "disabled:cursor-not-allowed disabled:opacity-50",
            )}
          >
            {selected ? (
              <>
                <PersonAvatar name={selected.name} size="sm" />
                <span className="flex min-w-0 flex-1 items-baseline gap-1.5 overflow-hidden whitespace-nowrap">
                  <span className="min-w-0 shrink truncate">
                    {selected.name}
                  </span>
                  {selectedMeta && (
                    <span className="min-w-0 shrink-[3] truncate text-xs text-text-secondary">
                      {selectedMeta}
                    </span>
                  )}
                </span>
              </>
            ) : (
              <span className="min-w-0 flex-1 truncate text-text-placeholder">
                {resolving ? "Loading…" : placeholder}
              </span>
            )}
            {/* Reserves the clear button's slot so the text truncates before it. */}
            {showClear && (
              <span className="h-6 w-6 shrink-0" aria-hidden="true" />
            )}
            <ChevronDown
              className={cn(
                "h-4 w-4 shrink-0 text-text-secondary transition-transform",
                open && "rotate-180",
              )}
              aria-hidden="true"
            />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          collisionPadding={8}
          className="w-[max(var(--radix-popover-trigger-width),20rem)] max-w-[calc(100vw-1rem)] overflow-hidden p-0"
        >
          <Command
            // Labels the search input (cmdk wires it via aria-labelledby).
            label="Search people"
            filter={filterResources}
            value={active}
            onValueChange={setActive}
            loop
          >
            <CommandInput
              value={search}
              onValueChange={setSearch}
              placeholder="Search by name or email…"
            />
            {isLoading ? (
              <div
                role="status"
                className="space-y-1 p-1"
                data-testid="resource-picker-loading"
              >
                <span className="sr-only">Loading people…</span>
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2.5 px-2 py-2"
                    aria-hidden="true"
                  >
                    <Skeleton className="h-7 w-7 shrink-0 rounded-full" />
                    <div className="flex-1 space-y-1.5">
                      <Skeleton className="h-3 w-2/5" />
                      <Skeleton className="h-2.5 w-3/4" />
                    </div>
                  </div>
                ))}
              </div>
            ) : isError ? (
              <div
                role="alert"
                className="flex flex-col items-center gap-1 px-4 py-6 text-center"
              >
                <p className="text-sm font-medium text-text-primary">
                  Couldn&apos;t load the people directory.
                </p>
                <p className="text-xs text-text-secondary">
                  Check your connection and try again.
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="mt-2"
                  onClick={() => void refetch()}
                >
                  Retry
                </Button>
              </div>
            ) : (
              <CommandList className="max-h-80" label="People">
                <CommandEmpty className="px-4 py-6 text-center">
                  <p className="text-sm font-medium text-text-primary">
                    {resources.length === 0
                      ? "No people available"
                      : "No matching people"}
                  </p>
                  <p className="mt-1 text-xs text-text-secondary">
                    {roleFiltered && wireRole
                      ? `${resources.length === 0 ? "" : "Try a different name or email. "}Only ${rolePlural(wireRole)}${pool === "delivery" ? " in the delivery pool" : ""} are listed.`
                      : resources.length === 0
                        ? pool === "delivery"
                          ? "Only active people in the delivery pool are listed."
                          : "No active people were found in the directory."
                        : pool === "delivery"
                          ? "Try a different name or email. Only people in the delivery pool are listed."
                          : "Try a different name or email."}
                  </p>
                </CommandEmpty>
                <CommandGroup>
                  {resources.map((r) => {
                    const summary = summarizeCurrentProjects(
                      r.current_projects,
                    );
                    const meta = describe(r);
                    const isSelected = value === r.id;
                    return (
                      <CommandItem
                        key={r.id}
                        value={r.id}
                        keywords={[r.name, r.email ?? "", r.designation ?? ""]}
                        onSelect={() => {
                          onChange(r.id, r.name);
                          handleOpenChange(false);
                        }}
                        className={cn(
                          "items-center gap-2.5 py-2 data-[selected=true]:text-text-primary",
                          // Highlight is a bar + tint, never tint alone.
                          "data-[selected=true]:shadow-[inset_3px_0_0_var(--color-primary)]",
                        )}
                      >
                        <PersonAvatar name={r.name} />
                        <span className="flex min-w-0 flex-1 flex-col">
                          <span className="flex min-w-0 items-center gap-2">
                            <span
                              className={cn(
                                "min-w-0 flex-1 truncate",
                                isSelected ? "font-semibold" : "font-medium",
                              )}
                            >
                              {r.name}
                            </span>
                            {isSelected && (
                              <>
                                <Check
                                  className="h-4 w-4 shrink-0 text-primary"
                                  aria-hidden="true"
                                />
                                <span className="sr-only">(selected)</span>
                              </>
                            )}
                          </span>
                          {(meta || summary) && (
                            <span className="flex min-w-0 items-center gap-1 text-xs text-text-secondary">
                              {meta && (
                                <span className="min-w-0 truncate">{meta}</span>
                              )}
                              {meta && summary && (
                                <span className="shrink-0" aria-hidden="true">
                                  ·
                                </span>
                              )}
                              {summary && (
                                <span
                                  className="min-w-0 truncate"
                                  title={
                                    summary.count > 0 ? summary.full : undefined
                                  }
                                  data-testid="resource-current-projects"
                                >
                                  {summary.text}
                                  {summary.truncated && (
                                    <span className="sr-only">
                                      {`. All projects: ${summary.full}`}
                                    </span>
                                  )}
                                </span>
                              )}
                            </span>
                          )}
                        </span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            )}
            {/* Fixed-height detail strip for the highlighted person: the full project list (with
                roles) that the row truncates. Redundant for screen readers (the option carries it
                as sr-only text), so it is hidden from them. */}
            {!isLoading && !isError && hasAssignmentData && highlighted && (
              <div
                className="h-11 border-t border-outlines bg-subtle-bg/60 px-3 py-1.5 text-xs leading-4 text-text-secondary"
                aria-hidden="true"
                data-testid="resource-picker-detail"
              >
                {/* One text node on purpose: a separate name element would duplicate the option's
                    name for anyone (or any test) looking the person up by text. */}
                <p className="line-clamp-2">
                  {`${highlighted.name} — ${
                    highlightedSummary && highlightedSummary.count > 0
                      ? highlightedSummary.full
                      : "Not on any project"
                  }`}
                </p>
              </div>
            )}
          </Command>
        </PopoverContent>
      </Popover>
      {showClear && (
        <button
          type="button"
          onClick={handleClear}
          aria-label={selected ? `Clear ${selected.name}` : "Clear selection"}
          className="absolute right-9 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-sm text-text-secondary hover:bg-subtle-bg hover:text-text-primary focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      )}
      {/* A <label htmlFor> names the trigger, which replaces its text as the accessible name — so
          the chosen person is exposed as the description instead. */}
      {selected && (
        <span id={valueId} className="sr-only">
          {`Selected: ${selected.name}${selectedMeta ? `, ${selectedMeta}` : ""}`}
        </span>
      )}
    </div>
  );
}

export default ResourcePicker;
